import { Shell } from "@bundle/core";
import { Button, ConnectivityBanner, Icon, PageListRow, ScrollView, Stack, Text, useFormat, useNavigation, usePageTree, useT, useTemplatesViewModel, useWorkspace } from "@bundle/ui";

/** /{ws}/projects — every database in the workspace (project boards first-class), and "New project board". */
export function ProjectsScreen() {
  const t = useT();
  const fmt = useFormat();
  const nav = useNavigation();
  const ws = useWorkspace();
  const tree = usePageTree();
  // The template gallery (featureFlags.templates; null when off).
  const gallery = useTemplatesViewModel();
  const dbUrl = (id: string) => Shell.formatUrl({ type: "db", ws: ws.slug, dbId: id });

  const create = async () => {
    const page = await tree.vm.create({ template: "projects" });
    if (page !== undefined) nav.open(dbUrl(page.id));
  };

  const list = tree.state.databases;
  return (
    <Stack grow testID="projects-screen">
      {ws.state.banner === undefined ? null : <ConnectivityBanner banner={ws.state.banner} />}
      <ScrollView padding="10" maxContentWidth="content.readable">
        <Stack gap="6">
          <Stack direction="horizontal" align="center" gap="4">
            <Stack grow gap="1">
              <Text variant="title1" weight="bold">
                {t("projects.title")}
              </Text>
              <Text variant="callout" color="text.secondary">
                {t("projects.subtitle")}
              </Text>
            </Stack>
            {tree.state.canCreatePages && gallery !== null ? (
              <Button label={t("templates.browse")} icon="sparkles" onPress={() => gallery.openGallery({ filter: "projects" })} testID="projects-templates" />
            ) : null}
            {tree.state.canCreatePages ? (
              <Button label={t("projects.new")} icon="plus" variant="primary" loading={tree.state.creating} onPress={() => void create()} testID="projects-new" />
            ) : null}
          </Stack>
          {tree.state.error === undefined ? null : (
            <Stack direction="horizontal" align="center" gap="2" padding="3" radius="md" background="bg.surface" border="border.subtle">
              <Icon name="alert" size="icon.sm" color="status.danger" />
              <Text variant="footnote" color="text.secondary">
                {t("docs.createFailed")}
              </Text>
            </Stack>
          )}
          {list.length === 0 && tree.state.status === "ready" ? (
            <Stack align="center" gap="3" padding="8" radius="lg" border="border.subtle" background="bg.canvas" testID="projects-empty">
              <Icon name="board" size="icon.lg" color="text.tertiary" />
              <Text variant="title3" weight="semibold" align="center">
                {t("projects.empty.title")}
              </Text>
              <Text color="text.secondary" align="center">
                {t("projects.empty.body")}
              </Text>
            </Stack>
          ) : (
            <Stack radius="lg" border="border.subtle" background="bg.canvas" padding="1" testID="projects-list">
              {list.map((p) => (
                <PageListRow
                  key={p.id}
                  title={p.title}
                  icon={p.icon}
                  kind={p.kind}
                  subtitle={t("page.edited", { time: fmt.timestamp(p.updatedAt) })}
                  onPress={() => nav.open(dbUrl(p.id))}
                  testID={`projects-row-${p.id}`}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </ScrollView>
    </Stack>
  );
}
